// filepath: apps/web/src/features/observability/widgets/ProcessTypeBreakdownWidget.tsx
//
// FR-UI-DASH-1 AC-1 widget 4 -- per-process_definition_id breakdown
// as horizontal bars (ISS-f4fa1b7b-268 resolved: the endpoint
// GET /api/metrics/instances-by-process-definition now backs this
// widget; the former `data-state="awaiting_fr_obs_3_ext"` typed
// placeholder is gone).
//
// Rows arrive as a prop from the page (the page owns the
// useProcessDefinitionBreakdown query + loading/error gating --
// mirrors widgets 1-3 which take `summary`). Bars are plain
// CSS-width divs (StepDurationHistogram precedent -- no
// third-party chart library).
//
// INV-9: the server-supplied `name` renders as a text node; zero
// `dangerouslySetInnerHTML`.
// INV-1: rows are already tenant-filtered server-side; the SPA
// renders exactly what it receives (INV-2).
// No drill-down link in v1: wiring a target for the reserved
// `process_definition_breakdown` MetricId is a separate UX
// decision (impact analysis §7).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { ProcessDefinitionBreakdownRow } from "../types";

export interface ProcessTypeBreakdownWidgetProps {
  /** Per-process_definition_id aggregate rows (server-emitted). */
  rows: ProcessDefinitionBreakdownRow[];
}

export function ProcessTypeBreakdownWidget({
  rows,
}: ProcessTypeBreakdownWidgetProps): React.ReactElement {
  const { t } = useTranslation();

  const maxCount = rows.reduce((m, r) => Math.max(m, r.instance_count), 0);
  const denom = Math.max(maxCount, 1);

  return (
    <article
      className="observability-widget observability-widget-process-breakdown"
      data-testid="observability-widget-process-breakdown"
      aria-labelledby="observability-widget-process-breakdown-title"
      data-tenant-scope="self"
      data-state={rows.length === 0 ? "empty" : undefined}
    >
      <h2
        id="observability-widget-process-breakdown-title"
        className="observability-widget-title"
      >
        {t(
          "observability.dashboard.widget.process_breakdown.title",
          "Instance count by process type",
        )}
      </h2>
      {rows.length === 0 ? (
        <p
          className="observability-widget-empty"
          data-testid="observability-widget-process-breakdown-empty"
        >
          {t(
            "observability.dashboard.widget.process_breakdown.empty",
            "No process instances yet.",
          )}
        </p>
      ) : (
        <dl className="observability-widget-breakdown">
          {rows.map((row) => (
            <BreakdownRow
              key={row.process_definition_id}
              row={row}
              denom={denom}
            />
          ))}
        </dl>
      )}
    </article>
  );
}

function BreakdownRow({
  row,
  denom,
}: {
  row: ProcessDefinitionBreakdownRow;
  denom: number;
}): React.ReactElement {
  const { t } = useTranslation();
  const widthPct = Math.min(
    100,
    Math.max(0, (row.instance_count / denom) * 100),
  );
  return (
    <div
      className="observability-widget-breakdown-row"
      data-testid="observability-widget-process-breakdown-row"
    >
      <dt className="observability-widget-breakdown-name">{row.name}</dt>
      <dd className="observability-widget-breakdown-bar-wrapper">
        <div
          className="observability-widget-breakdown-bar"
          style={{ width: `${widthPct.toFixed(2)}%` }}
        />
        <span className="observability-widget-breakdown-value">
          {row.instance_count}{" "}
          {t(
            "observability.dashboard.widget.process_breakdown.instances_label",
            "instances",
          )}
        </span>
        {row.failed_count > 0 && (
          <span className="observability-widget-breakdown-failed">
            {row.failed_count}{" "}
            {t(
              "observability.dashboard.widget.process_breakdown.failed_label",
              "failed",
            )}
          </span>
        )}
      </dd>
    </div>
  );
}
